import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Grid } from "@material-ui/core";
import httpClient from "../../lib/httpClient";
import { operationCancelled } from "../../lib/httpClient";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { UpdateMapButton } from "../util/UpdateMapButton";
import {
  tableCellRender,
  dateFilterOperators,
  numberFilterOperators,
  stringFilterOperators,
  isSingleSelectOperators,
  formatDateValue,
  getCompanyCodeName,
  getCompanyCodeFilter,
  getLatestUpdateMonth,
  filterModelToQueryValue,
} from "../util/tableUtils";
import { apiError } from "../../lib/apiError";
import { useClientConfig } from "../../context/clientConfigContext";

export const AnalyticsPerilsList = ({
  filterModel,
  setFilterModel,
  setFilteredMapData,
  wantedReferenceDateProp,
}) => {
  const { t } = useTranslation();
  const config = useClientConfig();

  const [sortModel, setSortModel] = useState({});
  const [policiesData, setPoliciesData] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const [lastUpdateDatePoliciesMsg, setLastUpdateDatePoliciesMsg] = useState("");
  const policiesCancelTokenRef = useRef(null);

  useEffect(() => {
    getLatestUpdateMonth("/serviceLogs/buildings?service=db_climate").then((lastUpdateMsg) => {
      setLastUpdateDatePoliciesMsg(lastUpdateMsg);
    });
  }, []);

  const getPoliciesData = useCallback(async () => {
    let sortField, sortDirection;
    const sortItem = sortModel;
    if (sortItem && Object.keys(sortItem).length) {
      const { field, sort } = sortItem[0];
      sortField = field;
      sortDirection = sort;
    }
    const requestPoliciesOfTheYear = `/analyticFilter/getClimateDbForYear?refDate=${wantedReferenceDateProp}&page=${
      paginationModel.page
    }&pageSize=${paginationModel.pageSize}&filters=${filterModelToQueryValue(
      filterModel,
    )}&orderBy=${sortField}&orderDirection=${sortDirection}`;

    try {
      setTableState((old) => ({ ...old, isLoading: true }));

      const { data: policiesResponse } = await httpClient().get(requestPoliciesOfTheYear, {
        cancelToken: policiesCancelTokenRef.current.token,
      });

      setPoliciesData(policiesResponse.data?.map((pol) => ({ ...pol, selected: false })));
      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: policiesResponse.total,
      }));
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        if (error.code === "ECONNABORTED") {
          console.error("Request timed out");
          apiError(error);
        } else {
          alert(
            error.message +
              ", we cannot access the backend server when loading policies data," +
              "  URL=" +
              httpClient().defaults.baseURL +
              "  request is " +
              requestPoliciesOfTheYear,
          );
          console.error("Error:", error);
        }

        setTableState((old) => ({
          ...old,
          isLoading: false,
          total: 0,
        }));
      }
    }
  }, [filterModel, sortModel, paginationModel.page, paginationModel.pageSize, wantedReferenceDateProp]);

  useEffect(() => {
    const getPoliciesDataWrapper = async () => {
      // Cancel the previous request when the filterModel changes
      if (policiesCancelTokenRef.current) {
        policiesCancelTokenRef.current.cancel(operationCancelled);
      }

      // Create a new cancel token for the current request
      policiesCancelTokenRef.current = axios.CancelToken.source();
      getPoliciesData();
    };

    if (filterModel) {
      const isAnyIncompleteFilter = filterModel?.items?.some((filter) => !filter?.value);

      if (!isAnyIncompleteFilter) {
        getPoliciesDataWrapper();
      }
    } else {
      getPoliciesDataWrapper();
    }
  }, [filterModel, getPoliciesData]);

  const onFilterChange = useCallback(
    (aFilterModel) => {
      setFilterModel(aFilterModel);
    },
    [setFilterModel],
  );

  const onSortChange = useCallback((aSortModel) => {
    setSortModel(aSortModel);
  }, []);

  // Experimental; should be obtained from the server
  const yearValueOptions = [wantedReferenceDateProp];

  const extraColumns =
    process.env.REACT_APP_ENVIRONMENT === "howden"
      ? [
          {
            field: "policyNumber",
            headerName: t("climateAnalytics.policies"),
            flex: 1,
            hide: true,
          },

          {
            field: "claimCount",
            headerName: t("climateAnalytics.NUMERO_SINISTRI"),
            flex: 1,
            filterOperators: numberFilterOperators,
            hide: true,
          },
        ]
      : [];

  const policiesColumns = [
    {
      headerName: t("climateAnalytics.referenceDate"),
      sortable: false,
      width: 70,
      field: "referenceDate",
      type: "singleSelect",
      filterable: false,
      filterOperators: isSingleSelectOperators,
      valueOptions: yearValueOptions,
    },

    {
      field: "companyCode" || "company",
      headerName: t("company"),
      width: 70,
      type: "singleSelect",
      filterOperators: isSingleSelectOperators,
      valueOptions: getCompanyCodeFilter(config.companyCodesMap),
      valueGetter: (params) => getCompanyCodeName(params.row.companyCode, config.companyCodesMap),
    },
    {
      field: "policyNumber",
      headerName: t("policy.primaryKey"),
      hide: true,
    },

    {
      field: "agencyName",
      headerName: t("agency"),
    },

    {
      field: "perilGroup",
      headerName: t("climateAnalytics.perilGroup"),
      sortable: false,
      width: 70,
    },
    {
      field: "perilName",
      headerName: t("climateAnalytics.perilName"),
      sortable: false,
      flex: 1,
    },

    {
      field: "sire",
      headerName: t("climateAnalytics.sire"),
      sortable: false,
      flex: 1,
    },

    ...extraColumns,

    {
      field: "productCommercial",
      headerName: t("commercialProduct"),
      flex: 1,
      sortable: false,
    },
    {
      field: "expiryDate",
      headerName: t("climateAnalytics.expiryDate"),
      type: "date",
      width: 100,
      filterOperators: dateFilterOperators,
      valueGetter: (params) => params.row.expiryDate && formatDateValue(params.row.expiryDate),
    },
    {
      field: "addressCity",
      headerName: t("city"),
      width: 180,
    },
    {
      field: "addressCityCode",
      headerName: t("cityCode"),
      sortable: false,
      width: 70,
    },
    {
      field: "addressProvince",
      headerName: t("state"),
      width: 20,
    },
    {
      field: "insuredValue",
      headerName: t("climateAnalytics.insuredValue"),
      width: 100,
      filterOperators: numberFilterOperators,
    },
  ];

  const columns = policiesColumns.map((tableColumn) => ({
    valueFormatter: (params) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,
    // flex: 1,
    ...tableColumn,
  }));

  return (
    <Grid item xs={12} sm={12}>
      <div style={{ width: "100%" }}>
        <DataGridCystellarTable
          density={"compact"}
          pageSizeOptions={[5, 25, 50, 100]}
          columns={columns}
          rows={policiesData}
          rowCount={tableState.total}
          getRowId={(rowData) => rowData._id}
          loading={tableState.isLoading}
          paginationMode={"server"}
          paginationModel={paginationModel}
          onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
          filterMode={"server"}
          onFilterModelChange={onFilterChange}
          sortingMode={"server"}
          onSortModelChange={onSortChange}
          customToolbarComp={true}
          customToolbarCompMsg={lastUpdateDatePoliciesMsg}
          customToolbarWidgets={
            <UpdateMapButton
              setFilteredMapData={setFilteredMapData}
              filterModel={filterModel}
              routeName="policies"
              variant="ClimateAnalytics"
              wantedReferenceDate={wantedReferenceDateProp}
            />
          }
          initialState={{
            columns: {
              columnVisibilityModel: {
                // Hide columns status and traderName, the other columns will remain visible
                policyNumber: false,
              },
            },
          }}
        />
      </div>
    </Grid>
  );
};
